import { notNil } from '@sixfold/typed-primitives';
import { default as DeckGL, WebMercatorViewport } from 'deck.gl';
import React from 'react';

import { useHighlightedStopIdContext } from './stop_highlight_context';
import {
  createRouteLayer,
  createStopLayer,
  createEventLayer,
  createVehicleBreaksLayer,
  createRulerLayer,
  createExternalEventLayer,
  createTourAreaLayer,
} from './tour_layer';
import { notEmpty } from '../../lib/graphql';
import { Viewport } from '../../map/components/map_viewport';
import {
  createVehiclesLayer,
  createGhostVehicleLayer,
  createVehiclesHistoryLayer,
} from '../../vehicle/components/vehicles_layer';
import { Vehicle, VehicleHistory, VehicleBreakHistory, VehicleStatus } from '../../vehicle/entities';
import { Route, Geofence, Event, Ruler, ExternalEvent, MappableStop, MapboxStyle, TrackingSegment } from '../entities';

type VehicleHistoryFilterMode = 'all' | 'tracking_ranges' | 'hide';
type VehicleBreakFilterMode = 'all' | 'tracking_ranges' | 'hide';

interface Props {
  rulers: Ruler[];
  stops: MappableStop[];
  route: Route;
  tourArea: Geofence | null;
  events: Event[];
  externalEvents: ExternalEvent[];
  ghostVehicles: VehicleHistory[];
  vehicles: (Vehicle & {
    history: VehicleHistory[];
    breakHistory: VehicleBreakHistory[];
    status: VehicleStatus | null;
  })[];
  viewport: Viewport;
  onViewportChange(viewport: Viewport): void;
  onAddRuler(lngLat: [number, number]): void;
  onLayerClick(coordinates: { lat: number | null; lng: number | null }): void;
  layersToHide?: {
    history: Record<string, boolean>;
    geofences: boolean;
    events: boolean;
    externalEvents: boolean;
    historyPoints: boolean;
    tourArea: boolean;
  };
  trackingSegments: TrackingSegment[];
  vehicleHistoryFilterMode: Record<string, VehicleHistoryFilterMode>;
  vehicleBreakFilterMode: Record<string, VehicleBreakFilterMode>;
  hideCurrentPosition: Record<string, boolean>;

  shouldDisplayCrosshairCursor: boolean;
  mapStyle: MapboxStyle;
}

export const TourOverlay: React.FC<Props> = ({
  stops,
  route,
  tourArea,
  rulers,
  shouldDisplayCrosshairCursor,
  events,
  externalEvents,
  vehicles,
  ghostVehicles,
  viewport,
  onViewportChange,
  onAddRuler,
  onLayerClick,
  layersToHide = {
    history: {},
    events: false,
    geofences: false,
    historyPoints: false,
    externalEvents: false,
    tourArea: false,
  },
  trackingSegments,
  vehicleHistoryFilterMode,
  vehicleBreakFilterMode,
  hideCurrentPosition,
  mapStyle,
}) => {
  const { highlightedStopId } = useHighlightedStopIdContext();
  const filteredVehicles = filterVehiclesHistory(vehicles, vehicleHistoryFilterMode, trackingSegments);
  const filteredGhostVehicles = filterGhostVehicles(
    ghostVehicles,
    vehicles,
    vehicleHistoryFilterMode,
    trackingSegments,
  );
  const visibleVehicles = vehicles.filter((vehicle) => !(hideCurrentPosition[vehicle.vehicle_id] ?? true));

  const { highlightedStopLayer, stopLabelsLayer, stopZonesLayers, stopIconsLayer } = createStopLayer({
    stops,
    onStopIconClick: onLayerClick,
    mapStyle,
    highlightedStopId,
  });
  const tourAreaLyer = createTourAreaLayer({ tourArea, mapStyle });
  const routeLayer = createRouteLayer({ route, mapStyle });
  const vehiclesLayer = createVehiclesLayer({
    vehicles: visibleVehicles,
    onClick: (vehicle) => onLayerClick({ lat: vehicle.status.latitude, lng: vehicle.status.longitude }),
  });

  const ghostVehicleLayer = filteredGhostVehicles.map(({ ghostVehicle, idx }) =>
    createGhostVehicleLayer(ghostVehicle, idx),
  );

  const { pointLayer: historyLayer, pathLayer: historyPathLayer } = createVehiclesHistoryLayer({
    vehicles: filteredVehicles,
    onClick: onLayerClick,
  });

  const { eventLabelsLayer, eventIconsLayer } = createEventLayer({ events, onClick: onLayerClick, mapStyle });
  const { externalEventLabelsLayer, externalEventIconsLayer } = createExternalEventLayer({
    externalEvents,
    onClick: onLayerClick,
  });

  const filteredBreaks = filterVehicleBreaks(vehicles, vehicleBreakFilterMode, trackingSegments);

  const { breakIconsLayer, breakLabelsLayer } = createVehicleBreaksLayer({
    vehicleBreaks: filteredBreaks,
    onBreakClick: onLayerClick,
    mapStyle,
  });

  const { rulersLayer, rulersLabelLayer, rulersTipsLayer } = createRulerLayer({
    rulers,
  });

  return (
    <DeckGL
      width={viewport.width}
      height={viewport.height}
      viewState={{
        longitude: viewport.longitude,
        latitude: viewport.latitude,
        zoom: viewport.zoom,
        pitch: viewport.pitch,
        bearing: viewport.bearing,
        maxZoom: viewport.maxZoom,
      }}
      controller={true}
      onViewStateChange={({ viewState }) =>
        onViewportChange({
          ...viewport,
          longitude: viewState.longitude,
          latitude: viewState.latitude,
          zoom: viewState.zoom,
          pitch: viewState.pitch,
          bearing: viewState.bearing,
        })
      }
      onLayerClick={(_info, _allInfos, srcEvent) => {
        if (!shouldDisplayCrosshairCursor || !srcEvent) {
          return;
        }
        const projection = new WebMercatorViewport({
          width: viewport.width,
          height: viewport.height,
          longitude: viewport.longitude,
          latitude: viewport.latitude,
          zoom: viewport.zoom,
          pitch: viewport.pitch,
          bearing: viewport.bearing,
        });
        const [lng, lat] = projection.unproject([srcEvent.offsetX, srcEvent.offsetY]);
        onAddRuler([lng, lat]);
      }}
      getCursor={({ isDragging }) => {
        if (shouldDisplayCrosshairCursor) {
          return 'crosshair';
        }
        return isDragging ? 'grabbing' : 'grab';
      }}
      layers={[
        highlightedStopLayer,
        stopLabelsLayer,
        ...(!layersToHide.geofences ? stopZonesLayers : []),
        !layersToHide.historyPoints ? historyLayer : null,
        routeLayer,
        !layersToHide.tourArea ? tourAreaLyer : null,
        historyPathLayer,
        !layersToHide.events ? eventIconsLayer : null,
        !layersToHide.externalEvents ? externalEventIconsLayer : null,
        breakIconsLayer,
        stopIconsLayer,
        ghostVehicleLayer,
        vehiclesLayer,
        !layersToHide.events ? eventLabelsLayer : null,
        !layersToHide.externalEvents ? externalEventLabelsLayer : null,
        breakLabelsLayer,
        rulersLayer,
        rulersLabelLayer,
        rulersTipsLayer,
      ].filter(notEmpty)}
    />
  );
};

interface ParsedTrackingSegment {
  vehicleId: string;
  startMs: number;
  endMs: number;
}

function parseTrackingSegment(segment: TrackingSegment): ParsedTrackingSegment {
  return {
    vehicleId: segment.vehicleId,
    startMs: new Date(segment.trackingStartDate).getTime(),
    endMs: new Date(segment.trackingEndDate).getTime(),
  };
}

function isWithinTrackingRanges(timestamp: string, vehicleTrackingSegments: TrackingSegment[]): boolean {
  if (vehicleTrackingSegments.length === 0) {
    return true;
  }

  const timestampMs = new Date(timestamp).getTime();
  const parsedSegments = vehicleTrackingSegments.map(parseTrackingSegment);

  return parsedSegments.some((segment) => {
    return timestampMs >= segment.startMs && timestampMs <= segment.endMs;
  });
}

function filterVehiclesHistory(
  vehicles: (Vehicle & { history: VehicleHistory[] })[],
  vehicleHistoryFilterMode: Record<string, VehicleHistoryFilterMode>,
  trackingSegments: TrackingSegment[],
): (Vehicle & { history: VehicleHistory[]; originalIndex: number })[] {
  return vehicles
    .map((vehicle, originalIndex) => {
      const mode = vehicleHistoryFilterMode[vehicle.vehicle_id] || 'all';

      if (mode === 'hide') {
        return null;
      }

      if (mode === 'tracking_ranges') {
        const vehicleTrackingSegments = trackingSegments.filter((segment) => segment.vehicleId === vehicle.vehicle_id);
        const filteredHistory = vehicle.history.filter((point) =>
          isWithinTrackingRanges(point.timestamp, vehicleTrackingSegments),
        );
        return { ...vehicle, history: filteredHistory, originalIndex };
      }

      return { ...vehicle, originalIndex };
    })
    .filter(notNil);
}

function filterGhostVehicles(
  ghostVehicles: VehicleHistory[],
  vehicles: (Vehicle & { history: VehicleHistory[] })[],
  vehicleHistoryFilterMode: Record<string, VehicleHistoryFilterMode>,
  trackingSegments: TrackingSegment[],
): { ghostVehicle: VehicleHistory; idx: number }[] {
  return ghostVehicles
    .map((ghostVehicle, idx) => {
      // Skip if ghostVehicle is undefined
      if (!ghostVehicle) {
        return null;
      }

      const vehicle = vehicles[idx];
      const filterMode = vehicleHistoryFilterMode[vehicle?.vehicle_id] || 'all';

      if (filterMode === 'hide') {
        return null;
      }

      if (filterMode === 'tracking_ranges') {
        const vehicleTrackingSegments = trackingSegments.filter((segment) => segment.vehicleId === vehicle.vehicle_id);
        const shouldInclude = isWithinTrackingRanges(ghostVehicle.timestamp, vehicleTrackingSegments);

        return shouldInclude ? { ghostVehicle, idx } : null;
      }

      return { ghostVehicle, idx };
    })
    .filter(notNil);
}

function isBreakWithinTrackingRanges(
  vehicleBreak: VehicleBreakHistory,
  vehicleTrackingSegments: TrackingSegment[],
): boolean {
  if (vehicleTrackingSegments.length === 0) {
    return true;
  }

  const breakStart = new Date(vehicleBreak.from).getTime();
  const breakEnd = vehicleBreak.to ? new Date(vehicleBreak.to).getTime() : Date.now();
  const parsedSegments = vehicleTrackingSegments.map(parseTrackingSegment);

  return parsedSegments.some((segment) => {
    return (
      (breakStart >= segment.startMs && breakStart <= segment.endMs) ||
      (breakEnd >= segment.startMs && breakEnd <= segment.endMs) ||
      (breakStart <= segment.startMs && breakEnd >= segment.endMs)
    );
  });
}

function filterVehicleBreaks(
  vehicles: (Vehicle & { breakHistory: VehicleBreakHistory[] })[],
  vehicleBreakFilterMode: Record<string, VehicleBreakFilterMode>,
  trackingSegments: TrackingSegment[],
): VehicleBreakHistory[] {
  return vehicles.reduce<VehicleBreakHistory[]>((combinedBreaks, vehicle) => {
    const mode = vehicleBreakFilterMode[vehicle.vehicle_id] || 'all';

    if (mode === 'hide') {
      return combinedBreaks;
    }

    if (mode === 'tracking_ranges') {
      const vehicleTrackingSegments = trackingSegments.filter((segment) => segment.vehicleId === vehicle.vehicle_id);
      const filteredBreaks = vehicle.breakHistory.filter((vehicleBreak) =>
        isBreakWithinTrackingRanges(vehicleBreak, vehicleTrackingSegments),
      );
      return [...combinedBreaks, ...filteredBreaks];
    }

    return [...combinedBreaks, ...vehicle.breakHistory];
  }, []);
}
